import {
  type AffineCommand,
  AffineCommandRegistry,
  type CommandCategory,
  PreconditionStrategy,
} from '@affine/core/commands';
import type { DocMode } from '@blocksuite/affine/model';
import { Entity, LiveData } from '@toeverything/infra';
import Fuse, { type FuseResultMatch } from 'fuse.js';

import { markQueryTerms } from '../../docs-search/services/query-terms';
import type { GlobalContextService } from '../../global-context';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchGroup } from '../types/group';
import { QUICK_SEARCH_RESULT_OWNER, type QuickSearchItem } from '../types/item';

const categories = {
  'affine:recent': {
    id: 'command:affine:recent',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.recent' },
    score: 10,
  },
  'affine:navigation': {
    id: 'command:affine:navigation',
    label: {
      i18nKey: 'com.streamlain.cmdk.affine.category.affine.navigation',
    },
    score: 10,
  },
  'affine:creation': {
    id: 'command:affine:creation',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.creation' },
    score: 10,
  },
  'affine:general': {
    id: 'command:affine:general',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.general' },
    score: 10,
  },
  'affine:layout': {
    id: 'command:affine:layout',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.layout' },
    score: 10,
  },
  'affine:pages': {
    id: 'command:affine:pages',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.pages' },
    score: 10,
  },
  'affine:edgeless': {
    id: 'command:affine:edgeless',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.edgeless' },
    score: 10,
  },
  'affine:collections': {
    id: 'command:affine:collections',
    label: {
      i18nKey: 'com.streamlain.cmdk.affine.category.affine.collections',
    },
    score: 10,
  },
  'affine:settings': {
    id: 'command:affine:settings',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.settings' },
    score: 10,
  },
  'affine:updates': {
    id: 'command:affine:updates',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.updates' },
    score: 10,
  },
  'affine:help': {
    id: 'command:affine:help',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.affine.help' },
    score: 10,
  },
  'editor:edgeless': {
    id: 'command:editor:edgeless',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.editor.edgeless' },
    score: 10,
  },
  'editor:insert-object': {
    id: 'command:editor:insert-object',
    label: {
      i18nKey: 'com.streamlain.cmdk.affine.category.editor.insert-object',
    },
    score: 10,
  },
  'editor:page': {
    id: 'command:editor:page',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.editor.page' },
    score: 10,
  },
  'affine:results': {
    id: 'command:affine:results',
    label: { i18nKey: 'com.streamlain.cmdk.affine.category.results' },
    score: 10,
  },
} satisfies Required<{
  [key in CommandCategory]: QuickSearchGroup & { id: `command:${key}` };
}>;

function filterCommandByContext(
  command: AffineCommand,
  context: {
    docMode: DocMode | undefined;
  }
) {
  if (command.preconditionStrategy === PreconditionStrategy.Always) {
    return true;
  }
  if (command.preconditionStrategy === PreconditionStrategy.InEdgeless) {
    return context.docMode === 'edgeless';
  }
  if (command.preconditionStrategy === PreconditionStrategy.InPaper) {
    return context.docMode === 'page';
  }
  if (command.preconditionStrategy === PreconditionStrategy.InPaperOrEdgeless) {
    return !!context.docMode;
  }
  if (command.preconditionStrategy === PreconditionStrategy.Never) {
    return false;
  }
  if (typeof command.preconditionStrategy === 'function') {
    return command.preconditionStrategy();
  }
  return true;
}

function getAllCommand(context: { docMode: DocMode | undefined }) {
  const commands = AffineCommandRegistry.getAll();
  return commands.filter(command => {
    return (
      command.navigation?.ownerId !== QUICK_SEARCH_RESULT_OWNER &&
      filterCommandByContext(command, context)
    );
  });
}

/** Optional work/input budget for automatic context; explicit search stays complete. */
export interface CommandNavigationSearchBudget {
  visit(reference?: string): boolean;
  text(value: string): string;
}
/** Search only semantic labels of admitted parents, never opaque action IDs. */
export function commandNavigationSearchText(
  command: AffineCommand,
  commands: ReadonlyMap<string, AffineCommand>,
  budget?: CommandNavigationSearchBudget
): string[] {
  const navigation = command.navigation;
  if (!navigation) return [];
  const definition = navigation.definition;
  const parts: string[] = [];
  const add = (value: string) => {
    if (budget && !budget.visit()) return false;
    if (value) parts.push(budget ? budget.text(value) : value);
    return true;
  };
  for (const value of [
    definition.label,
    definition.section ?? '',
    definition.description ?? '',
  ]) {
    if (!add(value)) return parts;
  }
  for (const keyword of definition.keywords) {
    if (!add(keyword)) return parts;
  }
  const ancestors: string[] = [];
  const visited = new Set([command.id]);
  const visit = (id: string): boolean => {
    if (budget && !budget.visit(id)) return false;
    if (visited.has(id)) return true;
    visited.add(id);
    const parent = commands.get(id)?.navigation;
    if (
      !parent ||
      parent.scope.workspaceId !== navigation.scope.workspaceId ||
      parent.scope.viewId !== navigation.scope.viewId ||
      !parent.definition.available()
    )
      return true;
    for (const parentId of parent.definition.parentIds) {
      if (!visit(parentId)) return false;
    }
    ancestors.push(
      budget ? budget.text(parent.definition.label) : parent.definition.label
    );
    return true;
  };
  for (const id of definition.parentIds) {
    if (!visit(id)) break;
  }
  return [
    ...new Set(
      [
        ...parts,
        ...ancestors,
        ...(ancestors.length
          ? [[...ancestors, definition.label].join(' ')]
          : []),
      ].filter(Boolean)
    ),
  ];
}

/** Shared existing matcher for User commands and scoped navigation discovery. */
export interface CommandSearchMatch {
  item: AffineCommand;
  matches?: readonly FuseResultMatch[];
  score?: number;
}
export function matchCommands(
  commands: readonly AffineCommand[],
  query: string
): CommandSearchMatch[] {
  // With no query, preserve the admitted commands and their order directly.
  // Building semantic paths and a Fuse index cannot change this result.
  if (!query) return commands.map(item => ({ item, matches: [], score: 0 }));
  const byId = new Map(commands.map(command => [command.id, command]));
  const entries = commands.map(item => ({
    item,
    label: item.label,
    navigationSearchText: commandNavigationSearchText(item, byId),
  }));
  const fuse = new Fuse(entries, {
    keys: [
      { name: 'label.title', weight: 2 },
      'label.subTitle',
      'navigationSearchText',
    ],
    includeMatches: true,
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.0,
  });
  return fuse.search(query).map(({ item, matches, score }) => ({
    item: item.item,
    matches,
    score,
  }));
}

export class CommandsQuickSearchSession
  extends Entity
  implements QuickSearchSession<'commands', AffineCommand>
{
  constructor(private readonly contextService: GlobalContextService) {
    super();
  }

  query$ = new LiveData('');

  /**
   * Docs the content results show; their sidebar entries stay out. Null while
   * the content search still answers the query: a sidebar entry of a doc would
   * show first and then give way to the doc's content row.
   */
  private readonly shownDocIds$ = new LiveData<ReadonlySet<string> | null>(
    new Set()
  );

  showsDocsElsewhere(docIds$: LiveData<ReadonlySet<string> | null>) {
    const subscription = docIds$.subscribe(ids => this.shownDocIds$.next(ids));
    this.disposables.push(() => subscription.unsubscribe());
  }

  items$ = LiveData.computed(get => {
    get(AffineCommandRegistry.changes$);
    const query = get(this.query$);
    const shownDocIds = get(this.shownDocIds$);
    const docMode =
      get(this.contextService.globalContext.docMode.$) ?? undefined;
    const commands = getAllCommand({ docMode }).filter(command => {
      const navigation = command.navigation?.definition;
      // The search does not offer opening itself.
      if (navigation?.capability === 'workspace-search') return false;
      const docId = navigation?.docId;
      return !docId || (shownDocIds !== null && !shownDocIds.has(docId));
    });

    const result = matchCommands(commands, query);

    // Fuse matches scattered characters; mark only the words that were typed.
    return result.map<QuickSearchItem<'commands', AffineCommand>>(
      ({ item, score = 1 }) => ({
        id: 'command:' + item.id,
        source: 'commands',
        label: {
          title: markQueryTerms(item.label.title, query) ?? item.label.title,
          subTitle: item.label.subTitle
            ? (markQueryTerms(item.label.subTitle, query) ??
              item.label.subTitle)
            : undefined,
        },
        group: categories[item.category],
        score:
          1 -
          score /* in fuse, the smaller the score, the better the match, so we need to reverse it */,
        icon: item.icon,
        keyBinding: item.keyBinding?.binding,
        payload: item,
      })
    );
  });

  query(query: string) {
    this.query$.next(query);
  }
}
